<template>
  <v-card flat>
    <v-dialog v-model="showDialog" persistent max-width="1100px">
      <v-card v-if="editItem">
        <v-card-title>{{ editItem.id ? 'Edit Category' : 'New Category' }}</v-card-title>
        <v-card-text>
          <v-row no-gutters>
            <v-col cols="12" md="7" class="pr-md-4">
              <v-form ref="form">
                <v-select
                  :value="positionAfterValue"
                  @input="setPositionAfter"
                  :items="positionAfterOptions"
                  label="Position"
                  hint="Choose which category this should come after"
                  persistent-hint
                />
                <v-text-field
                  v-model="editItem.name"
                  label="Name"
                  required
                  :rules="[v => !!v || 'Name is required']"
                />
                <v-textarea
                  v-model="editItem.description"
                  label="Description"
                  rows="2"
                  auto-grow
                />

                <v-divider class="my-4"/>
                <div class="text-subtitle-1 mb-2">Matching Criteria</div>

                <v-select
                  v-model="editItem.criteria.categories"
                  :items="categoryKeyOptions"
                  label="Source categories (keys in menu data)"
                  hint="Leave empty to consider meals from all categories"
                  persistent-hint
                  multiple
                  chips
                  small-chips
                  clearable
                />

                <v-combobox
                  v-model="editItem.criteria.tagsAny"
                  :items="sortedAvailableTags"
                  label="Match meals with ANY of these tags"
                  multiple
                  chips
                  small-chips
                  clearable
                  hint="Leave empty for no tag-any filter"
                  persistent-hint
                />

                <v-combobox
                  v-model="editItem.criteria.tagsAll"
                  :items="sortedAvailableTags"
                  label="Match meals with ALL of these tags"
                  multiple
                  chips
                  small-chips
                  clearable
                />

                <v-combobox
                  v-model="editItem.criteria.tagsNone"
                  :items="sortedAvailableTags"
                  label="Exclude meals with any of these tags"
                  multiple
                  chips
                  small-chips
                  clearable
                />

                <v-divider class="my-4"/>
                <div class="text-subtitle-1 mb-2">Sort Priority</div>
                <div class="text-caption mb-2" style="color:#888;">
                  Meals matching tags higher in the list appear first. Unmatched meals appear last.
                </div>
                <v-select
                  :items="sortPriorityTagOptions"
                  label="Add a tag to sort priority"
                  @input="addSortPriorityTag"
                  :value="null"
                  clearable
                  dense
                  hide-details
                  class="mb-2"
                />
                <div v-if="editItem.sortPriority && editItem.sortPriority.length">
                  <div
                    v-for="(tag, idx) in editItem.sortPriority"
                    :key="'sp-'+idx"
                    class="d-flex align-center mb-1"
                    style="background: #f5f5f5; border-radius: 4px; padding: 4px 8px;"
                  >
                    <span style="color:#888; font-size:12px; min-width:20px;">{{ idx + 1 }}.</span>
                    <v-chip small outlined color="orange" class="mx-1">{{ tag }}</v-chip>
                    <v-spacer/>
                    <v-btn icon x-small :disabled="idx === 0" @click="moveSortPriority(idx, -1)">
                      <v-icon small>mdi-arrow-up</v-icon>
                    </v-btn>
                    <v-btn icon x-small :disabled="idx === editItem.sortPriority.length - 1" @click="moveSortPriority(idx, 1)">
                      <v-icon small>mdi-arrow-down</v-icon>
                    </v-btn>
                    <v-btn icon x-small color="error" @click="removeSortPriority(idx)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
                <div v-else style="color:#999; font-size:12px; font-style:italic;">
                  No sort priority set — meals will appear in default order.
                </div>
              </v-form>
            </v-col>

            <v-col cols="12" md="5" class="pl-md-4" style="border-left: 1px solid #eee;">
              <div class="text-subtitle-1 mb-2">
                Live matches ({{ matchesForEdit.length }})
              </div>
              <div class="text-caption mb-2" style="color:#888;">
                Excludes expired meals.
              </div>
              <div v-if="matchesForEdit.length === 0" style="color:#999;font-size:12px;">
                <em>No meals match these criteria.</em>
              </div>
              <div v-else style="max-height: 60vh; overflow-y: auto;">
                <v-chip
                  v-for="meal in matchesForEdit"
                  :key="'matchedit-'+meal.id+'-'+meal.category"
                  x-small outlined class="ma-1"
                >
                  {{ meal.name }}
                  <span style="color:#999;margin-left:4px;">({{ meal.category }})</span>
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="editItem.id"
            outlined
            color="error"
            @click="confirmDelete"
          >Delete</v-btn>
          <v-spacer/>
          <v-btn outlined @click="showDialog = false">Cancel</v-btn>
          <v-btn outlined color="primary" :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card-title>
      Menu Categories
      <v-chip
        v-if="!loading"
        small
        outlined
        :color="hasUnpublishedChanges ? 'orange' : 'success'"
        :class="['ml-2', hasUnpublishedChanges ? 'clickable-chip' : '']"
        @click="hasUnpublishedChanges ? showDiffDialog = true : null"
      >
        {{ hasUnpublishedChanges ? 'Draft has unpublished changes — click to view' : 'Published — no changes' }}
      </v-chip>
      <v-spacer/>
      <v-btn
        outlined
        color="primary"
        class="mr-2"
        :loading="publishing"
        :disabled="!hasUnpublishedChanges"
        @click="publish"
      >
        <v-icon left small>mdi-cloud-upload</v-icon>
        Publish to live
      </v-btn>
      <v-btn outlined @click="newCategory">New Category</v-btn>
    </v-card-title>
    <v-card-subtitle class="pb-0" style="color:#888;">
      <template v-if="hasUnpublishedChanges">
        You are editing the draft. Click <strong>Publish to live</strong> to make these categories visible to users.
      </template>
      <template v-else>
        Draft matches live. Any edits will go to the draft — publish to make them go live.
      </template>
    </v-card-subtitle>

    <v-card-text>
      <div v-if="loading" class="text-center pa-4">
        <v-progress-circular indeterminate/>
      </div>
      <div v-else-if="categoriesSorted.length === 0" class="text-center pa-4" style="color:#999;">
        <em>No categories defined yet.</em>
      </div>
      <v-card
        v-for="cat in categoriesSorted"
        :key="cat.id"
        outlined
        class="mb-3"
      >
        <v-card-title>
          <span style="color:#888;font-size:14px;margin-right:8px;">#{{ cat.index }}</span>
          {{ cat.name }}
          <v-spacer/>
          <v-btn outlined small @click="editRow(cat)">
            <v-icon left small>mdi-pencil</v-icon>
            Edit
          </v-btn>
        </v-card-title>
        <v-card-subtitle v-if="cat.description">{{ cat.description }}</v-card-subtitle>
        <v-card-text>
          <div style="font-size: 12px; color: #333; margin-bottom: 8px;">
            <div v-if="cat.criteria && cat.criteria.categories && cat.criteria.categories.length">
              <strong>categories:</strong>
              <v-chip v-for="c in cat.criteria.categories" :key="'cat-'+c" x-small outlined color="primary" class="ml-1">{{ c }}</v-chip>
            </div>
            <div v-else style="color:#999;"><em>(any category)</em></div>
            <div v-if="cat.criteria && cat.criteria.tagsAny && cat.criteria.tagsAny.length">
              <strong>any of:</strong>
              <v-chip v-for="t in cat.criteria.tagsAny" :key="'any-'+t" x-small outlined color="orange" class="ml-1">{{ t }}</v-chip>
            </div>
            <div v-if="cat.criteria && cat.criteria.tagsAll && cat.criteria.tagsAll.length">
              <strong>all of:</strong>
              <v-chip v-for="t in cat.criteria.tagsAll" :key="'all-'+t" x-small outlined color="green" class="ml-1">{{ t }}</v-chip>
            </div>
            <div v-if="cat.criteria && cat.criteria.tagsNone && cat.criteria.tagsNone.length">
              <strong>none of:</strong>
              <v-chip v-for="t in cat.criteria.tagsNone" :key="'none-'+t" x-small outlined color="red" class="ml-1">{{ t }}</v-chip>
            </div>
            <div v-if="cat.sortPriority && cat.sortPriority.length">
              <strong>sort by:</strong>
              <v-chip v-for="(t, i) in cat.sortPriority" :key="'sp-'+t" x-small outlined color="purple" class="ml-1">{{ i + 1 }}. {{ t }}</v-chip>
            </div>
          </div>

          <v-divider class="mb-2"/>
          <div style="font-size: 12px; font-weight: 500; color: #666; margin-bottom: 4px;">
            Matching meals ({{ matchesFor(cat).length }})
          </div>
          <div v-if="matchesFor(cat).length === 0" style="color:#999;font-size:12px;">
            <em>No meals match.</em>
          </div>
          <div v-else>
            <v-chip
              v-for="meal in matchesFor(cat)"
              :key="'match-'+cat.id+'-'+meal.id+'-'+meal.category"
              x-small outlined class="ma-1"
            >
              {{ meal.name }}
              <span style="color:#999;margin-left:4px;">({{ meal.category }})</span>
            </v-chip>
          </div>
        </v-card-text>
      </v-card>
    </v-card-text>

    <v-dialog v-model="showDiffDialog" max-width="720px">
      <v-card>
        <v-card-title>
          Draft vs Live
          <v-spacer/>
          <v-btn icon @click="showDiffDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-subtitle>{{ diffEntries.length }} change{{ diffEntries.length === 1 ? '' : 's' }}</v-card-subtitle>
        <v-card-text>
          <div v-if="diffEntries.length === 0" style="color:#999;">
            <em>No differences.</em>
          </div>
          <div v-else>
            <div
              v-for="(entry, idx) in diffEntries"
              :key="idx"
              class="diff-entry"
            >
              <div class="d-flex align-center">
                <v-chip
                  x-small
                  dark
                  :color="entry.type === 'CREATE' ? 'green' : entry.type === 'REMOVE' ? 'red' : 'blue'"
                  class="mr-2"
                >{{ entry.type }}</v-chip>
                <strong v-if="entry.category">{{ entry.category }}</strong>
                <span v-if="entry.field" style="color:#666;" class="ml-2">{{ entry.field }}</span>
              </div>
              <div v-if="entry.type === 'CHANGE'" class="diff-values">
                <div class="diff-old">- {{ formatValue(entry.oldValue) }}</div>
                <div class="diff-new">+ {{ formatValue(entry.newValue) }}</div>
              </div>
              <div v-else-if="entry.type === 'REMOVE'" class="diff-values">
                <div class="diff-old">- {{ formatValue(entry.oldValue) }}</div>
              </div>
              <div v-else class="diff-values">
                <div class="diff-new">+ {{ formatValue(entry.newValue) }}</div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn outlined @click="showDiffDialog = false">Close</v-btn>
          <v-btn
            outlined
            color="primary"
            :loading="publishing"
            @click="showDiffDialog = false; publish()"
          >
            Publish to live
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="3000" color="success">
      {{ snackbarText }}
    </v-snackbar>
  </v-card>
</template>

<script>
import api from "@/api";
import diff from "microdiff";
import moment from "moment";
import {getNextOrderingDate} from "../store/utils";

// Normalises a category list for structural diff: sort by id, sort tag arrays,
// drop empty/undefined keys so order/absence of empty fields don't cause false diffs.
function normaliseForDiff(list) {
  const arr = Array.isArray(list) ? list : [];
  return arr
    .map(c => {
      const cr = c.criteria || {};
      const sortArr = (a) => (Array.isArray(a) ? [...a].sort() : []);
      return {
        id: c.id,
        index: Number(c.index) || 0,
        name: c.name || '',
        description: c.description || '',
        criteria: {
          categories: sortArr(cr.categories),
          tagsAny: sortArr(cr.tagsAny),
          tagsAll: sortArr(cr.tagsAll),
          tagsNone: sortArr(cr.tagsNone),
        },
        sortPriority: Array.isArray(c.sortPriority) ? [...c.sortPriority] : [],
      };
    })
    .sort((a, b) => (a.id || '').localeCompare(b.id || ''));
}

function matchesCriteria(meal, criteria) {
  if (!criteria) return true;
  const {categories, tagsAny, tagsAll, tagsNone} = criteria;
  if (Array.isArray(categories) && categories.length > 0) {
    if (!categories.includes(meal.category)) return false;
  }
  const mealTags = Array.isArray(meal.tags) ? meal.tags : [];
  if (Array.isArray(tagsAny) && tagsAny.length > 0) {
    if (!tagsAny.some(t => mealTags.includes(t))) return false;
  }
  if (Array.isArray(tagsAll) && tagsAll.length > 0) {
    if (!tagsAll.every(t => mealTags.includes(t))) return false;
  }
  if (Array.isArray(tagsNone) && tagsNone.length > 0) {
    if (tagsNone.some(t => mealTags.includes(t))) return false;
  }
  return true;
}

export default {
  name: "MenuCategoriesEdit",
  props: {
    availableTags: {type: Array, default: () => []},
    availableMenuCategoryKeys: {type: Array, default: () => []},
    menu: {type: Array, default: () => []},
  },
  data() {
    return {
      categories: [],
      livePublished: [],
      loading: false,
      saving: false,
      publishing: false,
      showDialog: false,
      showDiffDialog: false,
      editItem: null,
      snackbar: false,
      snackbarText: '',
    };
  },
  computed: {
    sortedAvailableTags() {
      return [...this.availableTags].sort((a, b) => a.localeCompare(b));
    },
    categoriesSorted() {
      return [...this.categories].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    },
    categoryKeyOptions() {
      return this.availableMenuCategoryKeys.map(k => ({text: k, value: k}));
    },
    positionAfterOptions() {
      const editId = this.editItem?.id;
      const others = this.categoriesSorted.filter(c => c.id !== editId);
      const options = [{text: '— First —', value: 0}];
      others.forEach(c => {
        options.push({text: `After "${c.name}"`, value: c.index});
      });
      return options;
    },
    positionAfterValue() {
      if (!this.editItem) return 0;
      const idx = this.editItem.index ?? 0;
      // Find the highest index among other categories that is less than editItem's index
      const editId = this.editItem.id;
      const others = this.categoriesSorted.filter(c => c.id !== editId && c.index < idx);
      if (others.length === 0) return 0;
      return others[others.length - 1].index;
    },
    sortPriorityTagOptions() {
      const current = this.editItem?.sortPriority || [];
      return this.sortedAvailableTags.filter(t => !current.includes(t));
    },
    matchesForEdit() {
      if (!this.editItem) return [];
      const meals = this.matchesFor(this.editItem);
      return this.sortBySortPriority(meals, this.editItem.sortPriority);
    },
    hasUnpublishedChanges() {
      return JSON.stringify(normaliseForDiff(this.categories))
        !== JSON.stringify(normaliseForDiff(this.livePublished));
    },
    diffEntries() {
      // Build map by id for both sides so microdiff produces stable, identifiable changes.
      const toMap = (list) => {
        const m = {};
        for (const c of normaliseForDiff(list)) m[c.id] = c;
        return m;
      };
      const liveMap = toMap(this.livePublished);
      const draftMap = toMap(this.categories);
      const changes = diff(liveMap, draftMap);
      // microdiff path[0] = category id; path[1..] = field path within category.
      const liveById = {};
      const draftById = {};
      for (const c of this.livePublished) liveById[c.id] = c;
      for (const c of this.categories) draftById[c.id] = c;
      return changes.map(ch => {
        const id = ch.path[0];
        const name = (draftById[id] && draftById[id].name) || (liveById[id] && liveById[id].name) || id;
        const field = ch.path.slice(1).join('.');
        return {
          type: ch.type,
          category: name,
          field,
          oldValue: ch.oldValue,
          newValue: ch.value,
        };
      });
    },
  },
  mounted() {
    return this.fetchCategories();
  },
  methods: {
    addSortPriorityTag(tag) {
      if (!tag) return;
      if (!this.editItem.sortPriority) this.$set(this.editItem, 'sortPriority', []);
      if (!this.editItem.sortPriority.includes(tag)) {
        this.editItem.sortPriority.push(tag);
      }
    },
    moveSortPriority(idx, direction) {
      const arr = this.editItem.sortPriority;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= arr.length) return;
      const temp = arr[idx];
      this.$set(arr, idx, arr[newIdx]);
      this.$set(arr, newIdx, temp);
    },
    removeSortPriority(idx) {
      this.editItem.sortPriority.splice(idx, 1);
    },
    sortBySortPriority(meals, sortPriority) {
      if (!sortPriority || !sortPriority.length) return meals;
      return [...meals].sort((a, b) => {
        const aIdx = this.getSortPriorityIndex(a, sortPriority);
        const bIdx = this.getSortPriorityIndex(b, sortPriority);
        return aIdx - bIdx;
      });
    },
    getSortPriorityIndex(meal, sortPriority) {
      const tags = Array.isArray(meal.tags) ? meal.tags : [];
      for (let i = 0; i < sortPriority.length; i++) {
        if (tags.includes(sortPriority[i])) return i;
      }
      return sortPriority.length; // unmatched meals go last
    },
    setPositionAfter(afterIndex) {
      // Place this category right after the selected one
      this.editItem.index = afterIndex + 1;
    },
    formatValue(v) {
      if (v === undefined || v === null) return '∅';
      if (Array.isArray(v)) return v.length === 0 ? '[]' : v.join(', ');
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    },
    isExpired(meal) {
      if (!meal || !meal.availableTo) return false;
      return moment(meal.availableTo).isBefore(moment(getNextOrderingDate()));
    },
    matchesFor(cat) {
      if (!cat) return [];
      return this.menu.filter(m => !this.isExpired(m) && matchesCriteria(m, cat.criteria));
    },
    async publish() {
      if (!confirm(`Publish ${this.categories.length} ${this.categories.length === 1 ? 'category' : 'categories'} to live? This will be visible to users immediately.`)) return;
      this.publishing = true;
      try {
        await api.post('v2/menu_categories/publish');
        this.livePublished = JSON.parse(JSON.stringify(this.categories));
        this.snackbarText = 'Published to live';
        this.snackbar = true;
      } catch (e) {
        console.error('failed to publish', e);
        this.snackbarText = 'Error publishing';
        this.snackbar = true;
      } finally {
        this.publishing = false;
      }
    },
    async fetchCategories() {
      this.loading = true;
      try {
        const [draftRes, liveRes] = await Promise.all([
          api.get('v2/menu_categories/draft'),
          api.get('v2/menu_categories'),
        ]);
        this.categories = Array.isArray(draftRes.data) ? draftRes.data : [];
        this.livePublished = Array.isArray(liveRes.data) ? liveRes.data : [];
      } catch (e) {
        console.error('failed to load categories', e);
      } finally {
        this.loading = false;
      }
    },
    newCategory() {
      this.editItem = {
        id: null,
        index: (this.categoriesSorted[this.categoriesSorted.length - 1]?.index ?? 0) + 1,
        name: '',
        description: '',
        criteria: {categories: [], tagsAny: [], tagsAll: [], tagsNone: []},
        sortPriority: [],
      };
      this.showDialog = true;
    },
    editRow(item) {
      const legacyCategory = item.criteria?.category;
      const categories = Array.isArray(item.criteria?.categories)
        ? [...item.criteria.categories]
        : (legacyCategory ? [legacyCategory] : []);
      this.editItem = {
        ...item,
        criteria: {
          categories,
          tagsAny: [...(item.criteria?.tagsAny || [])],
          tagsAll: [...(item.criteria?.tagsAll || [])],
          tagsNone: [...(item.criteria?.tagsNone || [])],
        },
        sortPriority: [...(item.sortPriority || [])],
      };
      this.showDialog = true;
    },
    rebalanceIndices() {
      // Sort categories by current index, then normalize to consecutive 1-based indices
      const sorted = [...this.categories].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
      sorted.forEach((cat, i) => {
        cat.index = i + 1;
      });
    },
    async save() {
      if (!this.editItem.name) {
        this.snackbarText = 'Name is required';
        this.snackbar = true;
        return;
      }
      this.saving = true;
      try {
        const desiredIndex = this.editItem.index ?? 0;
        const editId = this.editItem.id;

        // Shift existing categories if there's an index conflict
        const conflict = this.categories.find(c => c.id !== editId && c.index === desiredIndex);
        if (conflict) {
          this.categories.forEach(c => {
            if (c.id !== editId && c.index >= desiredIndex) {
              c.index++;
            }
          });
        }

        const {data} = await api.put('v2/menu_categories/draft', this.editItem);
        const idx = this.categories.findIndex(c => c.id === data.id);
        if (idx >= 0) {
          this.$set(this.categories, idx, data);
        } else {
          this.categories.push(data);
        }

        // Remove gaps so indices are consecutive
        this.rebalanceIndices();

        // Save all rebalanced indices to the server
        for (const cat of this.categories) {
          if (cat.id !== data.id) {
            await api.put('v2/menu_categories/draft', cat);
          }
        }

        this.snackbarText = 'Category saved';
        this.snackbar = true;
        this.showDialog = false;
      } catch (e) {
        console.error('failed to save category', e);
        this.snackbarText = 'Error saving category';
        this.snackbar = true;
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete() {
      if (!this.editItem?.id) return;
      if (!confirm(`Delete category "${this.editItem.name}"?`)) return;
      try {
        await api.delete(`v2/menu_categories/draft/${this.editItem.id}`);
        this.categories = this.categories.filter(c => c.id !== this.editItem.id);

        // Rebalance to close gaps
        this.rebalanceIndices();
        for (const cat of this.categories) {
          await api.put('v2/menu_categories/draft', cat);
        }

        this.snackbarText = 'Category deleted';
        this.snackbar = true;
        this.showDialog = false;
      } catch (e) {
        console.error('failed to delete category', e);
        this.snackbarText = 'Error deleting category';
        this.snackbar = true;
      }
    },
  },
};
</script>

<style scoped>
.clickable-chip {
  cursor: pointer;
}
.diff-entry {
  border-bottom: 1px solid #eee;
  padding: 8px 0;
}
.diff-entry:last-child {
  border-bottom: none;
}
.diff-values {
  font-family: monospace;
  font-size: 12px;
  margin-top: 4px;
  margin-left: 24px;
}
.diff-old {
  color: #c62828;
  background: #ffebee;
  padding: 2px 6px;
  border-radius: 3px;
  margin-bottom: 2px;
}
.diff-new {
  color: #2e7d32;
  background: #e8f5e9;
  padding: 2px 6px;
  border-radius: 3px;
}
</style>
